import type { AnyFunction } from '@/type-utils';
import { useRef, useInsertionEffect, useCallback } from 'react';

/**
 * A polyfill of the experimental `useEffectEvent` React hook.
 *
 * This returns a "type of function" known as an "Effect Event".
 * Effect events provide a way to separate non-reactive logic from
 * the reactive logic of an effect. They achieve this by always
 * referring to the latest version of any captured values. As a result,
 * effect events are conceptually similar to event handlers, however
 * they must only be triggered within effects.
 *
 * This hook was originally proposed in {@link https://github.com/reactjs/rfcs/pull/220 RFC: useEvent}, but the scope of
 * the feature and its semantics have slightly changed since then.
 *
 * **Note**: This is an experimental feature of React
 * and not guaranteed to be a long-term solution to the underlying
 * problem. Use with caution.
 *
 * @param fn - A function to treat as an effect event.
 * @returns A stable effect event function.
 * @see {@link https://react.dev/learn/separating-events-from-effects#declaring-an-effect-event Declaring an Effect Event}
 * @see {@link https://react.dev/learn/separating-events-from-effects#limitations-of-effect-events Limitations of Effect Events}
 * @see {@link https://github.com/bluesky-social/social-app/blob/main/src/lib/hooks/useNonReactiveCallback.ts Source}
 */
export function useEffectEvent<T extends AnyFunction>(fn: T): T {
  const ref = useRef<T>(fn);
  useInsertionEffect(() => {
    ref.current = fn;
  }, [fn]);
  return useCallback<T>(
    ((...args) => {
      const f = ref.current;
      return f(...args);
    }) as T,
    []
  );
}
